import Cookies from 'universal-cookie';
import { getProfile } from 'Api/AccountApi';
import { logout } from 'Api/ProfileApi';
import { createEntitlementCookie, getSubscriptionWithDelay, removeEntitlementCookie } from 'Utils/Entitlements';
import { identify } from 'Utils/Analytics';
import { clearAll } from 'State/GlobalActions';
import { trackPurchaseSource } from 'State/Tracking';
import { setSubscriptionPlanProperty, setSubscriptionStateProperty } from 'Utils/Analytics/Amplitude';
import { logger } from 'Utils/Logger';
import { createProfile } from 'Utils/ProfileSDK';
import { createAuthInstance } from 'Utils/AuthSDK';
import { getAnonymousId } from 'Utils/Analytics/SegmentShared';
import { getState, setState } from 'Utils/Store';
import { setBrazeUserLoggedIn } from 'Utils/Braze';
import { localStorageRemove } from 'Utils/Storage';
import { centralizedLoginSDK } from 'Utils/CentralizedLoginSDK';

export const userInitialState = {
  isAuthenticated: false, // non-anonymous user, identity account type
  isAnonymousUser: false,
  hasMvpd: false,
  mvpdId: '',
  profileId: null,
  userType: null,
  email: null,
  isFetching: false,
  entitlements: [],
  hasEntitlements: false,
  entitlementsV2: [],
  hasEntitlementsV2: false,
  pendingSignOut: false,
};

const MAX_RETRIES = 5;
const cookies = new Cookies();

export const updateUserProfile = (payload) =>
  setState('user', {
    email: payload.email,
    isFetching: false,
  });

const userSignIn = (payload) =>
  setState('user', {
    isAuthenticated: true,
    profileId: payload.profileId,
    userType: payload.userType,
    email: payload.email,
  });

const userSignInMvpd = (payload) =>
  setState('user', {
    isAuthenticated: false,
    isAnonymousUser: true,
    hasMvpd: true,
    mvpdId: payload.mvpd,
  });

const userPendingSignOut = () =>
  setState('user', {
    // used in RefreshTokenHandler as a flag to prevent multiple subsequent sign outs
    pendingSignOut: true,
  });

const userSignOut = () =>
  setState('user', {
    isAuthenticated: false,
    profileId: undefined,
    userType: undefined,
    email: undefined,
    pendingSignOut: false,
  });

const fetchingUser = () =>
  setState('user', {
    isFetching: true,
  });

const updateEntitlements = (payload) => setState('user', payload);

export const clearEntitlements = () =>
  setState('user', { entitlements: [], hasEntitlements: false, entitlementsV2: [], hasEntitlementsV2: false });

export const signIn = (user) => {
  const { lastKnownProfileIdCookieName, cookieDomain } = getState().appConfig.defaultConfig;
  const { profileId } = user;
  logger.started('sign in');
  userSignIn(user);
  cookies.set(lastKnownProfileIdCookieName, profileId, { path: '/', domain: cookieDomain });
  /* Passing the anonymousId makes the anonymousId stay consistent before and after sign up.
  https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity */
  identify(profileId, getAnonymousId());
  createProfile();
  logger.setAttr('profile_id', profileId);
  logger.completed('sign in');
};

export const signInMvpd = (user) => {
  const { lastKnownProfileIdCookieName, cookieDomain } = getState().appConfig.defaultConfig;
  const { mvpd } = user;
  logger.started('sign in mvpd');
  userSignInMvpd(user);
  cookies.remove(lastKnownProfileIdCookieName, { path: '/', domain: cookieDomain });
  logger.setAttr('mvpd', mvpd);
  logger.completed('sign in mvpd');
};

// we typically do a soft logout in the case of an error/non-explicit logout by the user
// https://teamfox.atlassian.net/wiki/spaces/PROFILE/pages/3012952177/Developer+Guide#Soft-Logout
export const signOut = (softLogout = false, centralAuthLogoutParams = {}) => {
  logger.started('sign out');
  return logout().then(async () => {
    userSignOut();
    const {
      subscription: { activeSubscriptionStatus },
    } = getState();
    if (activeSubscriptionStatus) {
      setSubscriptionStateProperty(activeSubscriptionStatus, true);
    }
    clearAll();
    localStorageRemove('successfulSubscriptionId');
    trackPurchaseSource();
    removeEntitlementCookie();
    identify();
    createProfile();
    createAuthInstance();
    setBrazeUserLoggedIn(false);
    if (!softLogout) {
      void (await centralizedLoginSDK().logout(centralAuthLogoutParams));
    }
    logger.completed('sign out', { activeSubscriptionStatus });
  });
};

export const pendingSignOut = () => {
  userPendingSignOut();
};

export const fetchProfileInfo = async () => {
  try {
    const {
      user: { profileId },
    } = getState();
    fetchingUser();
    const profileInfo = await getProfile(profileId);
    if (profileInfo && profileInfo.profileId) {
      updateUserProfile(profileInfo);
    } else {
      throw new Error('Error: cannot find profileId of ProfileInfo');
    }
  } catch (error) {
    //Necessary to set isFetching to false
    updateUserProfile({});
  }
};

export const getEntitlements = async (maxRetries = MAX_RETRIES) => {
  const {
    appConfig: {
      defaultConfig: { productSkus },
    },
  } = getState();

  const now = Date.now();
  logger.started('subscription poll', { productSkus });
  const { hasSubscription, subscriptions, hasSubscriptionV2, subscriptionsV2 } = await getSubscriptionWithDelay(
    productSkus,
    maxRetries
  );
  const diff = Date.now() - now; // in ms
  logger.completed('subscription poll', {
    pollTime: diff,
    hasSubscription,
    subscriptions,
    hasSubscriptionV2,
    subscriptionsV2,
  });
  if (hasSubscription || hasSubscriptionV2) {
    const [packageDetailsV1] = subscriptions;
    const [packageDetailsV2] = subscriptionsV2;
    const packageDetails = packageDetailsV1 ?? packageDetailsV2;
    setSubscriptionPlanProperty(packageDetails);
    trackPurchaseSource('general');
    await createEntitlementCookie();
    updateEntitlements({
      hasEntitlements: hasSubscription,
      entitlements: subscriptions,
      hasEntitlementsV2: hasSubscriptionV2,
      entitlementsV2: subscriptionsV2,
    });
    return { hasEntitlements: hasSubscription, hasEntitlementsV2: hasSubscriptionV2 };
  }
  // no entitlements, remove entitlement cookies
  trackPurchaseSource();
  removeEntitlementCookie();
  return { hasEntitlements: false, hasEntitlementsV2: false };
};
